import { Observable, Subscription, take } from 'rxjs';
import { ContentFilters, filtersKey, filtersMatch } from './dashboard-feed-filters';

export type ContentSection = 'news' | 'deals';

export interface ContentSearchPage {
  records: any[];
  hasMore: boolean;
  /** Raw API page when records are filtered locally; retain it for later searches. */
  cacheRecords?: any[];
}

export interface ContentSearchState {
  rows: any[];
  query: string;
  /** The filter panel's Date / Category / Created By, applied locally like the query. */
  filters: ContentFilters;
  loading: boolean;
  error: string;
  hasMore: boolean;
}

interface QueryCache {
  rows: any[];
  hasMore: boolean;
}

interface SectionControl {
  state: ContentSearchState;
  pool: any[];
  cache: Map<string, QueryCache>;
  version: number;
  nextPage: number;
  fetchedKeys: Set<string>;
  failedPage?: number;
  timer?: ReturnType<typeof setTimeout>;
  request?: Subscription;
}

function normalize(value: any): string {
  return String(value ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().toLocaleLowerCase();
}

/** Local matches are provisional: the API is still consulted for uncached items. */
export function contentMatches(record: any, query: string): boolean {
  const term = normalize(query);
  if (!term) return true;
  return normalize([
    record?.name, record?.title, record?.description, record?.details,
    record?.restaurant, record?.vendor?.title,
    ...(Array.isArray(record?.tagList) ? record.tagList : [])
  ].filter(Boolean).join(' ')).includes(term);
}

// Offers combine promotion and redemption tables, whose numeric IDs can overlap.
export function contentKey(section: ContentSection, record: any): string {
  const id = record?.id ?? record?.Id;
  const kind = section === 'deals' ? String(record?.type ?? record?.Type ?? '') : '';
  return id !== undefined && id !== null
    ? `${kind}:${id}`
    : `${kind}:${JSON.stringify([record?.name ?? record?.title, record?.image, record?.createdOn])}`;
}

function mergeRows(section: ContentSection, current: any[], incoming: any[]): any[] {
  const byId = new Map<string, any>();
  for (const record of [...current, ...incoming]) {
    if (record) byId.set(contentKey(section, record), record);
  }
  // Updating an existing key retains its position; newly discovered rows append.
  return Array.from(byId.values());
}

function criteriaKey(query: string, filters: ContentFilters): string {
  return normalize(query) + '|' + filtersKey(filters);
}

function createControl(): SectionControl {
  return {
    state: { rows: [], query: '', filters: {}, loading: false, error: '', hasMore: true },
    pool: [], cache: new Map(), version: 0, nextPage: 0, fetchedKeys: new Set()
  };
}

/**
 * Friday-style immediate cache results followed by guarded background reconciliation.
 * Reference: Evolution 2.0 docs/design/PAGE_DESIGN_PHILOSOPHY.md, cache-first UX;
 * view-tasks.component.ts guards responses by request version and full filter key.
 * Cached local matches never replace the API request needed for complete results.
 */
export class DashboardContentSearch {
  private readonly maxCachedRows = 160;
  private readonly maxCachedQueries = 12;
  private readonly controls: Record<ContentSection, SectionControl> = {
    news: createControl(), deals: createControl()
  };
  private destroyed = false;

  constructor(
    private readonly fetch: (section: ContentSection, query: string, page: number) => Observable<ContentSearchPage>,
    private readonly changed: () => void
  ) {}

  state(section: ContentSection): ContentSearchState {
    return this.controls[section].state;
  }

  seed(section: ContentSection, records: any[]): void {
    if (this.destroyed) return;
    const control = this.controls[section];
    const incoming = Array.isArray(records) ? records : [];
    // A late initial feed must not roll back rows already refreshed for this view.
    control.pool = mergeRows(section, incoming, control.pool).slice(-this.maxCachedRows);
    const visible = new Set(control.state.rows.map(row => contentKey(section, row)));
    control.state.rows = mergeRows(section, control.state.rows,
      incoming.filter(row => !visible.has(contentKey(section, row)) && this.matches(section, row)));
    this.changed();
  }

  /** Every record seen for a section, unfiltered (the filter panel lists its choices from these). */
  records(section: ContentSection): any[] {
    return this.controls[section].pool.slice();
  }

  search(section: ContentSection, query: string): void {
    this.apply(section, query, this.controls[section].state.filters);
  }

  filter(section: ContentSection, filters: ContentFilters): void {
    this.apply(section, this.controls[section].state.query, filters || {});
  }

  private apply(section: ContentSection, query: string, filters: ContentFilters): void {
    if (this.destroyed) return;
    const control = this.controls[section];
    this.cancel(control);
    const key = criteriaKey(query, filters);
    const cached = control.cache.get(key);
    if (cached) {
      control.cache.delete(key);
      control.cache.set(key, cached);
    }
    // Keep the editable value intact: trimming it here removes spaces mid-typing.
    control.state.query = query || '';
    control.state.filters = filters;
    control.state.rows = mergeRows(section, cached?.rows || [],
      control.pool.filter(row => this.matches(section, row)));
    control.state.error = '';
    control.state.hasMore = cached?.hasMore ?? true;
    control.state.loading = true;
    control.nextPage = 0;
    control.fetchedKeys.clear();
    control.failedPage = undefined;
    this.changed();
    control.timer = setTimeout(() => {
      control.timer = undefined;
      this.request(section, 0);
    }, 350);
  }

  refresh(section: ContentSection): void {
    if (this.destroyed) return;
    const control = this.controls[section];
    this.cancel(control);
    control.nextPage = 0;
    this.request(section, 0);
  }

  loadMore(section: ContentSection): void {
    const control = this.controls[section];
    if (this.destroyed || control.state.loading || !control.state.hasMore) return;
    this.request(section, control.nextPage);
  }

  retry(section: ContentSection): void {
    const control = this.controls[section];
    if (this.destroyed || control.state.loading) return;
    this.request(section, control.failedPage ?? 0);
  }

  destroy(): void {
    this.destroyed = true;
    Object.values(this.controls).forEach(control => {
      this.cancel(control);
      control.pool = [];
      control.cache.clear();
      control.fetchedKeys.clear();
    });
  }

  private matches(section: ContentSection, row: any): boolean {
    const { query, filters } = this.controls[section].state;
    return contentMatches(row, query) && filtersMatch(section, row, filters);
  }

  private cancel(control: SectionControl): void {
    control.version++;
    clearTimeout(control.timer);
    control.timer = undefined;
    control.request?.unsubscribe();
    control.request = undefined;
    control.state.loading = false;
  }

  private request(section: ContentSection, page: number): void {
    const control = this.controls[section];
    const version = ++control.version;
    const query = control.state.query.trim();
    const isCurrent = () => !this.destroyed && control.version === version;
    control.state.loading = true;
    control.state.error = '';
    control.failedPage = undefined;
    this.changed();
    let received = false;
    const fail = () => {
      if (!isCurrent()) return;
      control.state.loading = false;
      control.state.error = 'Could not refresh results. Please try again.';
      control.failedPage = page;
      this.changed();
    };
    try {
      const subscription = this.fetch(section, query, page).pipe(take(1)).subscribe({
        next: result => {
          if (!isCurrent()) return;
          received = true;
          const records = Array.isArray(result?.records) ? result.records : [];
          // Offers' server Q match is case-sensitive. Its adapter filters bounded
          // unfiltered pages locally and supplies the raw page for cache/progress.
          const cacheRecords = Array.isArray(result?.cacheRecords) ? result.cacheRecords : records;
          if (page === 0) control.fetchedKeys.clear();
          const keys = cacheRecords.filter(Boolean).map(row => contentKey(section, row));
          const hasNewPageRows = keys.some(key => !control.fetchedKeys.has(key));
          keys.forEach(key => control.fetchedKeys.add(key));
          // The adapter matched the query; the panel filters apply here.
          control.state.rows = mergeRows(section, control.state.rows,
            records.filter(row => filtersMatch(section, row, control.state.filters)));
          control.pool = mergeRows(section, control.pool, cacheRecords).slice(-this.maxCachedRows);
          // A backend repeating a page must not allow an endless load-more loop.
          // Compare this traversal's responses, since cached later pages may be visible.
          control.state.hasMore = !!result?.hasMore && (page === 0 || hasNewPageRows);
          control.state.loading = false;
          control.nextPage = page + 1;
          this.cache(control, criteriaKey(query, control.state.filters));
          this.changed();
        },
        error: fail,
        complete: () => { if (!received) fail(); }
      });
      control.request = subscription.closed ? undefined : subscription;
    } catch {
      fail();
    }
  }

  private cache(control: SectionControl, key: string): void {
    control.cache.delete(key);
    control.cache.set(key, {
      rows: control.state.rows.slice(0, this.maxCachedRows), hasMore: control.state.hasMore
    });
    while (control.cache.size > this.maxCachedQueries) {
      control.cache.delete(control.cache.keys().next().value);
    }
  }
}
